import { SendTestMessageRequest } from "@/pages/Private/pages/Account/schema/sendTestMessage";

import { Draft } from "./inboxDraft.types";

/**
 * Maps an inbox-chat draft to the payload accepted by
 * `POST schedule/send-test-message`.
 *
 * The channel (and therefore the request variant) is determined by the draft's
 * sender type — the chat only ever composes `email` or `linkedin` messages, which
 * are exactly the two channels the test-message endpoint supports. Everything except
 * the test recipient is taken from the draft; the recipient is supplied by the user.
 * An email test carries the draft's Include signature choice, or the company default when the
 * user never toggled it, so the test shows what the send will (PREC-4401).
 */
export const mapDraftToTestMessageRequest = (
	draft: Draft,
	listPersonId: number,
	recipient: string,
	signatureDefault = false
): SendTestMessageRequest => {
	if (draft.from?.type === "email") {
		return {
			channel: "email",
			listPersonId,
			message: draft.content,
			senderUserId: draft.from.userId,
			subject: draft.subject ?? "",
			senderEmailId: draft.from.emailId ?? 0,
			recipientEmail: recipient,
			includeSignature: draft.includeSignature ?? signatureDefault,
		};
	}

	return {
		channel: "linkedin",
		listPersonId,
		message: draft.content,
		senderUserId: draft.from.userId,
		recipientLinkedinUrl: recipient,
	};
};

/**
 * Whether a draft holds enough valid data to be sent as a test message.
 *
 * Gates the "Send test" trigger button. The test endpoint requires a non-empty
 * message for every channel (so empty invite notes are not testable), a concrete
 * sender, matching sender/recipient channels, and — for email — a sender email id
 * plus a subject line.
 */
export const isDraftTestable = (draft?: Draft): boolean => {
	if (!draft?.from?.userId) {
		return false;
	}

	if (!draft.content?.trim()) {
		return false;
	}

	const senderType = draft.from.type;
	const recipientType = draft.to?.type;

	if (senderType && recipientType && senderType !== recipientType) {
		return false;
	}

	if (senderType === "email") {
		return !!draft.from.emailId && !!draft.subject?.trim();
	}

	return true;
};
